
import React, { useState, useEffect, createContext, useContext } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { onAuthStateChanged, User } from '@firebase/auth';
import { ref, onValue } from '@firebase/database';
import { auth, db } from './firebase';
import { Product, Category, CartItem, UserProfile, Role } from './types';
import { LayoutDashboard, ShoppingBag, Layers, LogOut, Megaphone, ClipboardList, Truck, MessageCircle, Headphones, Users, TicketPercent, BarChart3, UserCog, ShieldCheck, KeyRound, Settings } from 'lucide-react';

// Components
import Header from './components/Header';
import SideCart from './components/SideCart';
import Footer from './components/Footer';
import LuminaAI from './components/LuminaAI';

// Pages
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import AuthPage from './pages/AuthPage';

// Info Pages
import AboutPage from './pages/AboutPage';
import WorkshopPage from './pages/WorkshopPage';
import ContactPage from './pages/ContactPage';
import ShippingPage from './pages/ShippingPage';
import ReturnsPage from './pages/ReturnsPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';

// Admin Pages
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminProducts from './pages/Admin/AdminProducts';
import AdminCategories from './pages/Admin/AdminCategories';
import AdminAnnouncements from './pages/Admin/AdminAnnouncements';
import AdminOrders from './pages/Admin/AdminOrders';
import AdminCargo from './pages/Admin/AdminCargo';
import AdminSupport from './pages/Admin/AdminSupport';
import AdminCustomers from './pages/Admin/AdminCustomers';
import AdminCoupons from './pages/Admin/AdminCoupons';
import AdminReports from './pages/Admin/AdminReports';
import AdminStaff from './pages/Admin/AdminStaff';
import AdminRoles from './pages/Admin/AdminRoles';
import AdminSettings from './pages/Admin/AdminSettings';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const WhatsAppButton = () => {
  return (
    <a 
      href="https://wa.me/905362311888" 
      target="_blank" 
      rel="noopener noreferrer"
      className="fixed bottom-8 left-8 z-[90] bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform active:scale-95 group flex items-center space-x-2"
    >
      <MessageCircle size={28} fill="currentColor" />
      <div className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 ease-in-out whitespace-nowrap">
        <span className="text-sm font-bold pr-2">Canlı Destek</span>
      </div>
    </a>
  );
};

interface AppContextType {
  user: User | null;
  profile: UserProfile | null;
  cart: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  categories: Category[];
  products: Product[];
  roles: Record<string, Role>;
  isCartOpen: boolean;
  setIsCartOpen: (isOpen: boolean) => void;
}

export const AppContext = createContext<AppContextType | null>(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

const RoleGuard: React.FC<{ children: React.ReactNode, pageId: string, profile: UserProfile | null, roles: Record<string, Role> }> = ({ children, pageId, profile, roles }) => {
  if (!profile) return <Navigate to="/admin/login" replace />;
  if (profile.isAdmin === true || profile.role === 'admin') return <>{children}</>;
  if (pageId === 'dashboard') return <>{children}</>;
  const userRole = profile.role || 'user';
  const userRoleData = roles[userRole];
  if (userRoleData && userRoleData.permissions && userRoleData.permissions.includes(pageId)) {
    return <>{children}</>;
  }
  return <Navigate to="/admin/dashboard" replace />;
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [roles, setRoles] = useState<Record<string, Role>>({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        onValue(ref(db, `users/${u.uid}`), (snapshot) => {
          setProfile(snapshot.val());
          setInitializing(false);
        }, (err) => {
          setInitializing(false);
        });
      } else {
        setProfile(null);
        setInitializing(false);
      }
    });

    onValue(ref(db, 'categories'), (snapshot) => {
      const data = snapshot.val();
      if (data) setCategories(Object.values(data));
    });

    onValue(ref(db, 'products'), (snapshot) => {
      const data = snapshot.val();
      if (data) setProducts(Object.values(data));
    });

    onValue(ref(db, 'roles'), (snapshot) => {
      const data = snapshot.val();
      if (data) setRoles(data);
    });

    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        setCart([]);
      }
    }

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: Product, quantity: number = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => item.product.id === product.id ? { ...item, quantity: item.quantity + quantity } : item);
      }
      return [...prev, { product, quantity }];
    });
    setIsCartOpen(true); 
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    setCart(prev => prev.map(item => item.product.id === productId ? { ...item, quantity: Math.max(1, quantity) } : item));
  };

  const clearCart = () => setCart([]);

  if (initializing) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-[#FFFCF8]">
        <div className="w-12 h-12 border-4 border-amber-100 border-t-amber-800 rounded-full animate-spin"></div>
        <p className="mt-4 text-stone-400 font-bold uppercase tracking-widest text-[10px]">Lumina Home Hazırlanıyor</p>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ 
      user, profile, cart, addToCart, removeFromCart, 
      updateCartQuantity, clearCart, categories, products, roles,
      isCartOpen, setIsCartOpen 
    }}>
      <HashRouter>
        <ScrollToTop />
        <div className="flex flex-col min-h-screen">
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={profile?.isAdmin ? <AdminLayout profile={profile} roles={roles} /> : <Navigate to="/admin/login" />} />
            
            <Route path="/*" element={
              <>
                <Header />
                <SideCart />
                <WhatsAppButton />
                <LuminaAI />
                <main className="flex-grow pt-24 md:pt-32 bg-[#FFFCF8]">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/category/:slug" element={<CategoryPage />} />
                    <Route path="/product/:id" element={<ProductDetailPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/auth" />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/workshop" element={<WorkshopPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/shipping" element={<ShippingPage />} />
                    <Route path="/returns" element={<ReturnsPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </main>
                <Footer />
              </>
            } />
          </Routes>
        </div>
      </HashRouter>
    </AppContext.Provider>
  );
};

const AdminLayout: React.FC<{ profile: UserProfile, roles: Record<string, Role> }> = ({ profile, roles }) => {
  return (
    <div className="flex h-screen bg-stone-100 overflow-hidden font-sans">
      <AdminSidebar profile={profile} roles={roles} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Routes>
          <Route path="dashboard" element={<RoleGuard pageId="dashboard" profile={profile} roles={roles}><AdminDashboard /></RoleGuard>} />
          <Route path="products" element={<RoleGuard pageId="products" profile={profile} roles={roles}><AdminProducts /></RoleGuard>} />
          <Route path="categories" element={<RoleGuard pageId="categories" profile={profile} roles={roles}><AdminCategories /></RoleGuard>} />
          <Route path="announcements" element={<RoleGuard pageId="announcements" profile={profile} roles={roles}><AdminAnnouncements /></RoleGuard>} />
          <Route path="orders" element={<RoleGuard pageId="orders" profile={profile} roles={roles}><AdminOrders /></RoleGuard>} />
          <Route path="cargo" element={<RoleGuard pageId="cargo" profile={profile} roles={roles}><AdminCargo /></RoleGuard>} />
          <Route path="support" element={<RoleGuard pageId="support" profile={profile} roles={roles}><AdminSupport /></RoleGuard>} />
          <Route path="customers" element={<RoleGuard pageId="customers" profile={profile} roles={roles}><AdminCustomers /></RoleGuard>} />
          <Route path="coupons" element={<RoleGuard pageId="coupons" profile={profile} roles={roles}><AdminCoupons /></RoleGuard>} />
          <Route path="reports" element={<RoleGuard pageId="reports" profile={profile} roles={roles}><AdminReports /></RoleGuard>} />
          <Route path="staff" element={<RoleGuard pageId="staff" profile={profile} roles={roles}><AdminStaff /></RoleGuard>} />
          <Route path="roles" element={<RoleGuard pageId="roles" profile={profile} roles={roles}><AdminRoles /></RoleGuard>} />
          <Route path="settings" element={<RoleGuard pageId="settings" profile={profile} roles={roles}><AdminSettings /></RoleGuard>} />
          <Route path="*" element={<Navigate to="dashboard" />} />
        </Routes>
      </div>
    </div>
  );
};

const AdminSidebar: React.FC<{ profile: UserProfile, roles: Record<string, Role> }> = ({ profile, roles }) => {
  const location = useLocation();
  const userRoleData = roles[profile?.role || 'user'];
  const userPermissions = (profile?.role === 'admin' || profile?.isAdmin) ? null : (userRoleData?.permissions || []);

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Panel', path: '/admin/dashboard' },
    { id: 'orders', icon: ClipboardList, label: 'Siparişler', path: '/admin/orders' },
    { id: 'reports', icon: BarChart3, label: 'Raporlar', path: '/admin/reports' },
    { id: 'customers', icon: Users, label: 'Müşteriler', path: '/admin/customers' },
    { id: 'products', icon: ShoppingBag, label: 'Ürünler', path: '/admin/products' },
    { id: 'categories', icon: Layers, label: 'Kategoriler', path: '/admin/categories' },
    { id: 'coupons', icon: TicketPercent, label: 'Kuponlar', path: '/admin/coupons' },
    { id: 'cargo', icon: Truck, label: 'Kargo', path: '/admin/cargo' },
    { id: 'support', icon: Headphones, label: 'Destek', path: '/admin/support' },
    { id: 'announcements', icon: Megaphone, label: 'Duyurular', path: '/admin/announcements' },
    { id: 'staff', icon: UserCog, label: 'Personel', path: '/admin/staff' },
    { id: 'roles', icon: KeyRound, label: 'Roller', path: '/admin/roles' },
    { id: 'settings', icon: Settings, label: 'Ayarlar', path: '/admin/settings' },
  ];

  const filteredMenu = menuItems.filter(item => 
    profile?.role === 'admin' || profile?.isAdmin || userPermissions?.includes(item.id)
  );

  return (
    <div className="w-64 bg-stone-900 text-stone-100 flex flex-col">
      <div className="p-6 font-bold text-2xl tracking-tighter border-b border-stone-800">
        Lumina<span className="text-amber-500">Admin</span>
        <div className="text-[10px] text-amber-500/50 uppercase tracking-widest mt-1 font-black">
          {profile?.role === 'admin' || profile?.isAdmin ? 'TAM YETKİLİ' : (userRoleData?.name || 'YETKİLİ')}
        </div>
      </div> 
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-hide">
        {filteredMenu.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${location.pathname.includes(item.path) ? 'bg-amber-700 text-white shadow-lg' : 'text-stone-400 hover:bg-stone-800 hover:text-white'}`}
          >
            <item.icon size={20} />
            <span className="text-sm font-bold">{item.label}</span>
          </Link>
        ))}
      </nav>
      <button 
        onClick={() => auth.signOut()}
        className="p-6 flex items-center space-x-3 text-red-400 hover:bg-stone-800 transition-colors border-t border-stone-800"
      >
        <LogOut size={20} />
        <span className="text-sm font-bold">Güvenli Çıkış</span>
      </button>
    </div>
  );
};

export default App;
